import React from 'react';
import { PensionInputs as PensionInputsType } from '../Interfaces';
import { ValidatedInput } from '../Components';

interface PensionInputsProps {
  inputs: PensionInputsType;
  onChange: (inputs: PensionInputsType) => void;
  getFieldError: (fieldName: string) => string | undefined;
  hasFieldError: (fieldName: string) => boolean;
}

export const PensionInputs: React.FC<PensionInputsProps> = ({
  inputs,
  onChange,
  getFieldError,
  hasFieldError
}) => {
  const handleChange = (field: keyof PensionInputsType, value: number) => {
    onChange({
      ...inputs,
      [field]: value,
    });
  };

  return (
    <div className={`controls pension-controls ${hasFieldError('startAmount') || hasFieldError('startAge') || hasFieldError('endAge') || hasFieldError('monthlyAmount') || hasFieldError('annualIncrease') || hasFieldError('annualReturn') || hasFieldError('taxRelief') || hasFieldError('employerContrib') ? 'has-errors' : ''}`}>
      <h2>🏛️ Pension</h2>
      <div className="control-group">
        <ValidatedInput
          label="Starting Amount (£)"
          value={inputs.startAmount}
          onChange={(value) => handleChange('startAmount', value)}
          error={getFieldError('startAmount')}
          min="0"
          max="10000000"
        />

        <ValidatedInput
          label="Starting Age"
          value={inputs.startAge}
          onChange={(value) => handleChange('startAge', value)}
          error={getFieldError('startAge')}
          min="16"
          max="75"
        />

        <ValidatedInput
          label="Retirement Age"
          value={inputs.endAge}
          onChange={(value) => handleChange('endAge', value)}
          error={getFieldError('endAge')}
          min="50"
          max="85"
          tooltip="The UK State Pension age is currently 66, rising to 67 between 2026 and 2028. You can retire before or after this — this field is your personal target retirement age, not the State Pension age."
        />

        <ValidatedInput
          label="Monthly Contribution (£)"
          value={inputs.monthlyAmount}
          onChange={(value) => handleChange('monthlyAmount', value)}
          error={getFieldError('monthlyAmount')}
          min="1"
          max="40000"
        />

        <ValidatedInput
          label="Annual Increase (%)"
          value={inputs.annualIncrease}
          onChange={(value) => handleChange('annualIncrease', value)}
          error={getFieldError('annualIncrease')}
          step="0.5"
          min="0"
          max="20"
          tooltip="Increase your contributions each year to keep pace with wage growth. UK average annual wage growth has historically been 3–4%. Setting this helps reflect real-world contribution growth over your career."
        />

        <ValidatedInput
          label="Annual Return (%)"
          value={inputs.annualReturn}
          onChange={(value) => handleChange('annualReturn', value)}
          error={getFieldError('annualReturn')}
          step="0.1"
          min="0"
          max="25"
          tooltip="UK pension funds have historically returned around 5–8% per year. A commonly used assumption is 7% for a balanced portfolio, though returns vary by fund type and risk level."
        />

        <ValidatedInput
          label="Tax Relief Rate (%)"
          value={inputs.taxRelief}
          onChange={(value) => handleChange('taxRelief', value)}
          error={getFieldError('taxRelief')}
          min="0"
          max="45"
          tooltip="The government tops up your pension contributions with tax relief at your marginal Income Tax rate. Basic rate taxpayers get 20%, higher rate 40%, and additional rate 45%. A £100 contribution costs a basic rate taxpayer just £80."
        />

        <ValidatedInput
          label="Employer Contribution (%)"
          value={inputs.employerContrib}
          onChange={(value) => handleChange('employerContrib', value)}
          error={getFieldError('employerContrib')}
          step="0.5"
          min="0"
          max="25"
          tooltip="The minimum employer contribution under auto-enrolment is 3% of qualifying earnings. Many employers match higher contributions — check your employment contract or HR policy to find your exact entitlement."
        />

        <ValidatedInput
          label="Drawdown Rate (%/year)"
          value={inputs.withdrawalRate}
          onChange={(value) => handleChange('withdrawalRate', value)}
          error={getFieldError('withdrawalRate')}
          step="0.5"
          min="0.1"
          max="15"
          tooltip="UK pension income drawdown lets you withdraw flexibly while your fund stays invested. A common rule of thumb is 4% per year — at this rate a balanced fund often lasts indefinitely. Higher rates increase income but risk depleting the pot earlier."
        />
      </div>
    </div>
  );
};